import { memo, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import TuneIcon from "@mui/icons-material/Tune";
import PaymentMethodLimitsForm, * as PaymentMethodLimits from "ui/forms/Merchant/PaymentMethodLimits";
import { FormAdapter } from "hooks/useForm";
import { convertObjectToNumberValues } from "util/transformers";
import useMerchantPaymentMethods from "./useMerchantPaymentMethods";

type Props = {
    selectedIds: Set<string>;
    onClear: () => void;
};

const BulkEditPaymentMethodLimits = ({ selectedIds, onClear }: Props) => {
    const [open, setOpen] = useState(false);
    const { repository } = useMerchantPaymentMethods();
    const selectedMethods = useMemo(() => repository.merchantsMethods.filter(method =>
        selectedIds.has(String(method.coreId)) && Boolean(method.paymentMethodIsAssignable)
    ), [repository.merchantsMethods, selectedIds]);
    const template = selectedMethods[0];
    const selectedMethodNames = selectedMethods.map(method =>
        `${method.paymentMethodTag} · ${method.paymentMethodPSP}`
    );

    const form = PaymentMethodLimits.useForm({
        initialValues: PaymentMethodLimits.getInitialValues(template),
        onSaveOrCreate: async values => {
            let lastResponse: Awaited<ReturnType<typeof repository.updateMethodLimits>> | undefined;
            for (const method of selectedMethods) {
                lastResponse = await repository.updateMethodLimits({
                    ...convertObjectToNumberValues(values),
                    coreId: method.coreId
                });
            }
            setOpen(false);
            onClear();
            return FormAdapter.fromResponse(lastResponse!);
        }
    });

    return (
        <>
            <Button
                variant='outlined'
                startIcon={<TuneIcon />}
                disabled={!selectedMethods.length}
                onClick={() => setOpen(true)}
            >
                Edit limits
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='lg'>
                <DialogTitle>
                    Bulk edit limits · {selectedMethods.length} methods
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {selectedMethodNames.join(', ')}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity='info' sx={{ mb: 2 }}>
                        Values start from the first selected method. Saving applies every field below to all selected methods.
                    </Alert>
                    {template && (
                        <PaymentMethodLimitsForm
                            {...template}
                            {...form}
                            onCancel={() => setOpen(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default memo(BulkEditPaymentMethodLimits);
